import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Avatar,
    Box,
    Collapse,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {
    CheckCircleRounded,
    ErrorOutlineRounded,
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import { LOBBY_EXIT_BUTTON_SX } from "../common/dialogActionButtonSx";
import ConfirmDialog from "../common/ConfirmDialog.jsx";
import Loader from "../common/loader";
import UploadButton from "./UploadField";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import {
    pickAvatarUpdatedAtMs,
    resolveUserAvatarSrc,
} from "../../utils/avatarUrl";
import {
    ACCOUNT_PAGE_FIELD_ACTIONS_SX,
    ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_DIVIDER_SX,
    ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX,
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_FIELD_ROW_SX,
    ACCOUNT_PAGE_FIELD_SAVE_BUTTON_SX,
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_MOBILE_MQ,
    ACCOUNT_PAGE_SEND_CODE_BUTTON_DISABLED_SX,
    ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_ARROW_SX,
    ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX,
    ACCOUNT_PAGE_SUBSECTION_SX,
    ACCOUNT_PAGE_SUBSECTION_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX,
} from "./accountPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const AVATAR_ACTION_ROW_SX = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.6rem",
    "& label": {
        display: "inline-flex",
    },
    "& .MuiButton-root": {
        ...ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
        m: "0 !important",
        ml: "0 !important",
    },
    "& .MuiButton-endIcon": {
        ml: "0.5rem",
        "& svg": { fontSize: "1rem" },
    },
};

const SUBSECTION_COLLAPSE_TIMEOUT = { enter: 320, exit: 260 };
const basePath = utils.getEdgeBasePath();
const SUBSECTION_COLLAPSE_EASING = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const SOCIAL_FIELDS = [
    {
        key: "bio",
        label: "Bio",
        placeholder: "Write a short bio",
        maxLength: 250,
        multiline: true,
    },
    {
        key: "website",
        label: "Website",
        placeholder: "Website URL",
        maxLength: 300,
    },
    {
        key: "twitter",
        label: "X (Twitter)",
        placeholder: "X (Twitter) account URL",
        maxLength: 300,
    },
    {
        key: "youtube",
        label: "YouTube",
        placeholder: "YouTube account URL",
        maxLength: 300,
    },
    {
        key: "tiktok",
        label: "TikTok",
        placeholder: "TikTok account URL",
        maxLength: 300,
    },
    {
        key: "instagram",
        label: "Instagram",
        placeholder: "Instagram account URL",
        maxLength: 300,
    },
];

const DISCORD_ERROR_MESSAGES = {
    already_linked: "That Discord account is already connected to another Ouro account.",
    not_configured: "Discord connection is not configured on this server.",
    missing_params: "Discord did not return all required connection details.",
    state: "The Discord connection request expired. Please try again.",
    start: "Discord connection could not be started. Please try again.",
};

function getStringValue(value) {
    return typeof value === "string" ? value : "";
}

function ProfilePictureEditor({ user, actions, avatarCacheBust }) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
    const [pendingAvatar, setPendingAvatar] = useState(null);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmedAvatarVersion, setConfirmedAvatarVersion] = useState(null);

    const persistedAvatarVersion = useMemo(
        () => pickAvatarUpdatedAtMs(user, avatarCacheBust),
        [avatarCacheBust, user],
    );
    const savedAvatarUser = useMemo(() => {
        if (!Number.isFinite(confirmedAvatarVersion)) return user;

        return {
            ...user,
            avatar:
                user?.avatar && typeof user.avatar === "object"
                    ? {
                          ...user.avatar,
                          updatedAtMs: confirmedAvatarVersion,
                      }
                    : { updatedAtMs: confirmedAvatarVersion },
            avatarUpdatedAtMs: confirmedAvatarVersion,
        };
    }, [confirmedAvatarVersion, user]);

    const savedAvatarSrc = useMemo(
        () =>
            resolveUserAvatarSrc({
                basePath,
                user: savedAvatarUser,
                avatarCacheBust:
                    confirmedAvatarVersion ?? avatarCacheBust,
            }),
        [avatarCacheBust, confirmedAvatarVersion, savedAvatarUser],
    );
    const avatarSrc = avatarPreviewUrl || savedAvatarSrc || user?.picture;
    const uploading = uploadStatus?.state === "load";
    const hasPendingAvatar = Boolean(pendingAvatar);

    useEffect(
        () => () => {
            if (
                avatarPreviewUrl &&
                typeof URL !== "undefined" &&
                typeof URL.revokeObjectURL === "function"
            ) {
                URL.revokeObjectURL(avatarPreviewUrl);
            }
        },
        [avatarPreviewUrl],
    );

    const handleUploadStatusChange = useCallback(
        ({ name, state, text }) => {
            if (name !== "avatar") return;
            setUploadStatus({ state, text });
        },
        [],
    );

    const handleUploadComplete = useCallback(({ file, name, tmpFilename }) => {
        if (name !== "avatar" || !file || !tmpFilename) return;

        const nextPreviewUrl = URL.createObjectURL(file);
        setAvatarPreviewUrl(nextPreviewUrl);
        setPendingAvatar({
            file: file.name,
            tmpFilename,
        });
    }, []);

    const cancelAvatarChange = useCallback(() => {
        if (saving || uploading) return;
        setAvatarPreviewUrl(null);
        setPendingAvatar(null);
        setUploadStatus(null);
    }, [saving, uploading]);

    const saveAvatar = useCallback(async () => {
        if (
            !pendingAvatar ||
            saving ||
            uploading ||
            typeof actions?.submitAccountChanges !== "function"
        ) {
            if (!saving && !uploading && pendingAvatar) {
                toast.error("Unable to save your profile picture.");
            }
            return;
        }

        setSaving(true);
        try {
            const result = await actions.submitAccountChanges({
                userId: user?.userId,
                avatar: pendingAvatar,
            });
            if (result?.type !== "ACCOUNT_UPDATE_COMPLETE") {
                throw new Error(
                    result?.text || "Failed to update your profile picture.",
                );
            }

            const authoritativeUser =
                result?.response?.result?.authoritativeUser;
            const nextAvatarVersion = pickAvatarUpdatedAtMs(
                authoritativeUser,
                result?.response?.result,
                result?.response,
            );

            if (
                !Number.isFinite(nextAvatarVersion) ||
                nextAvatarVersion === persistedAvatarVersion
            ) {
                throw new Error(
                    "The new profile picture could not be saved. Please try again.",
                );
            }

            setConfirmedAvatarVersion(nextAvatarVersion);
            setPendingAvatar(null);
            setAvatarPreviewUrl(null);
            setUploadStatus(null);
            toast.success("Profile picture updated successfully");
        } catch (error) {
            setUploadStatus({
                state: "error",
                text:
                    error?.message ||
                    "Failed to update your profile picture.",
            });
            toast.error(
                error?.message || "Failed to update your profile picture.",
            );
        } finally {
            setSaving(false);
        }
    }, [
        actions,
        pendingAvatar,
        persistedAvatarVersion,
        saving,
        uploading,
        user?.userId,
    ]);

    const renderAvatarActionButtons = () => (
        <>
            {!hasPendingAvatar ? (
                <UploadButton
                    name="avatar"
                    onUploadComplete={handleUploadComplete}
                    onUploadStatusChange={handleUploadStatusChange}
                    disableMargin
                    hideStatus
                    disabled={saving}
                    fileSizeLimitInMB={5}
                    iconPosition="end"
                    buttonText="Change"
                />
            ) : null}

            {hasPendingAvatar ? (
                <>
                    <Box
                        component="button"
                        type="button"
                        disabled={saving || uploading}
                        onClick={saveAvatar}
                        sx={{
                            ...ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
                            m: "0 !important",
                            ml: "0 !important",
                            minHeight: "2.35rem",
                            "&:disabled": {
                                opacity: 0.5,
                                cursor: "not-allowed",
                            },
                        }}
                    >
                        {saving ? "Saving" : "Save picture"}
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        disabled={saving || uploading}
                        onClick={cancelAvatarChange}
                        sx={{
                            ...ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX,
                            height: "2.35rem",
                        }}
                    >
                        Cancel
                    </Box>
                </>
            ) : null}
        </>
    );

    return (
        <Box
            sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: "8rem minmax(0, 1fr)",
                alignItems: "center",
                gap: { xs: "1.25rem", sm: "1.75rem" },
                width: "100%",
                maxWidth: "48rem",
                p: { xs: "1.15rem", sm: "1.35rem 1.5rem" },
                boxSizing: "border-box",
                overflow: "hidden",
                borderRadius: "14px",
                border: `1px solid ${alpha(colors.primary.main, 0.13)}`,
                background: `
                    radial-gradient(
                        circle at 8% 18%,
                        ${alpha(colors.primary.main, 0.075)},
                        transparent 35%
                    ),
                    linear-gradient(
                        145deg,
                        rgba(17,17,20,0.96) 0%,
                        rgba(10,10,12,0.985) 58%,
                        rgba(7,7,9,1) 100%
                    )
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.045),
                    inset 0 -36px 70px rgba(0,0,0,0.2),
                    0 16px 38px rgba(0,0,0,0.18)
                `,
                "&::before": {
                    content: '\"\"',
                    position: "absolute",
                    top: 0,
                    left: "7%",
                    width: "22%",
                    height: "1px",
                    background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.48)}, transparent)`,
                    boxShadow: `0 0 14px ${alpha(colors.primary.main, 0.18)}`,
                    pointerEvents: "none",
                },
                [ACCOUNT_PAGE_MOBILE_MQ]: {
                    gridTemplateColumns: "1fr",
                    alignItems: "stretch",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.65rem",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "6.8rem",
                        height: "6.8rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        border: `1px solid ${alpha(colors.accent.main, 0.28)}`,
                        background: `radial-gradient(circle, ${alpha(colors.primary.main, 0.13)}, rgba(255,255,255,0.02) 68%)`,
                        boxShadow: `
                            inset 0 1px 0 rgba(255,255,255,0.065),
                            0 10px 26px rgba(0,0,0,0.34),
                            0 0 24px ${alpha(colors.primary.main, 0.08)}
                        `,
                    }}
                >
                    <Avatar
                        src={avatarSrc || undefined}
                        alt={user?.nickname || user?.name || "Profile picture"}
                        sx={{
                            width: "6rem",
                            height: "6rem",
                            border: "2px solid rgba(255,255,255,0.055)",
                            backgroundColor: alpha(colors.text.primary, 0.08),
                        }}
                    />

                    {uploading || saving ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: "0.38rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                backgroundColor: "rgba(4,4,6,0.68)",
                                backdropFilter: "blur(3px)",
                            }}
                        >
                            <Loader size={28} />
                        </Box>
                    ) : null}

                    {hasPendingAvatar && !uploading && !saving ? (
                        <Box
                            sx={{
                                position: "absolute",
                                right: 2,
                                bottom: 4,
                                width: "1.65rem",
                                height: "1.65rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                border: "2px solid rgba(8,8,10,0.96)",
                                color: colors.text.contrast,
                                background: colors.gradient.accent,
                                boxShadow: colors.glow.soft,
                            }}
                        >
                            <CheckCircleRounded sx={{ fontSize: "1.05rem" }} />
                        </Box>
                    ) : null}
                </Box>

                <Typography
                    sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: hasPendingAvatar
                            ? colors.accent.main
                            : colors.text.muted,
                    }}
                >
                    {hasPendingAvatar ? "New preview" : "Current picture"}
                </Typography>

                {isMobile ? (
                    <Box
                        sx={{
                            ...AVATAR_ACTION_ROW_SX,
                            justifyContent: "center",
                            width: "100%",
                            mt: "0.2rem",
                            "& .MuiButton-root": {
                                ...ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
                                m: "0 !important",
                                ml: "0 !important",
                                minHeight: "2rem",
                                py: "0.32rem",
                                px: "1.7rem",
                                minWidth: "9.5rem",
                                borderRadius: "6px",
                            },
                        }}
                    >
                        {renderAvatarActionButtons()}
                    </Box>
                ) : null}
            </Box>

            <Box
                sx={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                }}
            >
                <Typography
                    component="h3"
                    sx={{
                        color: colors.text.primary,
                        fontSize: { xs: "1rem", sm: "1.08rem" },
                        fontWeight: 700,
                        lineHeight: 1.2,
                    }}
                >
                    Profile picture
                </Typography>
                <Typography
                    sx={{
                        maxWidth: "32rem",
                        mt: "0.35rem",
                        color: colors.text.secondary,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        lineHeight: 1.55,
                    }}
                >
                    Choose a clear image that represents you across your public
                    profile, lobbies, and matchrooms.
                </Typography>
                <Typography
                    sx={{
                        mt: "0.55rem",
                        color: colors.text.muted,
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        lineHeight: 1.35,
                        letterSpacing: "0.04em",
                    }}
                >
                    JPG, PNG or WEBP · Maximum 5 MB
                </Typography>

                {!isMobile ? (
                    <Box
                        sx={{
                            ...AVATAR_ACTION_ROW_SX,
                            mt: "1rem",
                        }}
                    >
                        {renderAvatarActionButtons()}
                    </Box>
                ) : null}

                {uploadStatus?.state === "error" ? (
                    <Box
                        role="alert"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            mt: "0.8rem",
                            color: "#FCA5A5",
                            fontSize: "0.74rem",
                            fontWeight: 600,
                        }}
                    >
                        <ErrorOutlineRounded sx={{ fontSize: "1rem" }} />
                        Avatar upload failed: {uploadStatus.text}
                    </Box>
                ) : null}

            </Box>
        </Box>
    );
}

ProfilePictureEditor.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
    avatarCacheBust: PropTypes.number,
};

function DiscordIcon() {
    return (
        <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
        >
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
    );
}

function SocialDetailField({
    field,
    value,
    editing,
    draftValue,
    saving,
    onEdit,
    onDraftChange,
    onSave,
    onCancel,
}) {
    const inputId = `public-profile-${field.key}`;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <Typography
                component="label"
                htmlFor={inputId}
                sx={ACCOUNT_PAGE_FIELD_LABEL_SX}
            >
                {field.label}
            </Typography>
            <Box
                sx={{
                    ...ACCOUNT_PAGE_FIELD_ROW_SX,
                    alignItems: field.multiline ? "flex-start" : "center",
                }}
            >
                <Box sx={ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX}>
                    <TextField
                        id={inputId}
                        variant="outlined"
                        value={editing ? draftValue : value || "Not set"}
                        placeholder={editing ? field.placeholder : undefined}
                        disabled={!editing}
                        onChange={
                            editing
                                ? (event) => onDraftChange(event.target.value)
                                : undefined
                        }
                        autoFocus={editing}
                        multiline={field.multiline}
                        minRows={field.multiline ? 3 : undefined}
                        maxRows={field.multiline ? 6 : undefined}
                        sx={OUTLINED_INPUT_SX}
                        slotProps={{
                            input: { readOnly: !editing },
                            htmlInput: {
                                "aria-label": field.label,
                                readOnly: !editing,
                                maxLength: field.maxLength,
                                autoComplete: "off",
                                autoCorrect: "off",
                                spellCheck: field.multiline,
                            },
                        }}
                    />
                </Box>
                {!editing ? (
                    <Box
                        component="button"
                        type="button"
                        aria-label={`Edit ${field.label.toLowerCase()}`}
                        onClick={onEdit}
                        sx={ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX}
                    >
                        Edit
                    </Box>
                ) : null}
            </Box>
            {field.multiline && editing ? (
                <Box
                    sx={{
                        ...ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography
                        aria-live="polite"
                        sx={{
                            color:
                                draftValue.length >= field.maxLength
                                    ? colors.primary.main
                                    : colors.text.muted,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            lineHeight: 1.2,
                            letterSpacing: "0.04em",
                            mt: "0.1rem",
                        }}
                    >
                        {draftValue.length} / {field.maxLength}
                    </Typography>
                </Box>
            ) : null}
            <Collapse in={editing} timeout={180}>
                <Box sx={ACCOUNT_PAGE_FIELD_ACTIONS_SX}>
                    <Box
                        component="button"
                        type="button"
                        disabled={saving}
                        onClick={onSave}
                        sx={ACCOUNT_PAGE_FIELD_SAVE_BUTTON_SX}
                    >
                        {saving ? "Saving" : "Save"}
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        disabled={saving}
                        onClick={onCancel}
                        sx={ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX}
                    >
                        Cancel
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}

SocialDetailField.propTypes = {
    field: PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        maxLength: PropTypes.number,
        multiline: PropTypes.bool,
    }).isRequired,
    value: PropTypes.string,
    editing: PropTypes.bool,
    draftValue: PropTypes.string,
    saving: PropTypes.bool,
    onEdit: PropTypes.func.isRequired,
    onDraftChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

function DiscordField({ user, actions }) {
    const [unlinking, setUnlinking] = useState(false);
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const connected = Boolean(user?.discordLinked);
    const displayName = connected
        ? user?.discordUsername ||
          user?.discordGlobalName ||
          "Discord account"
        : "";

    const handleConnect = useCallback(() => {
        window.location.href = `${basePath}/auth/discord`;
    }, []);

    const handleDisconnect = useCallback(async () => {
        if (unlinking) return;
        setUnlinking(true);

        try {
            const response = await fetch(
                `${basePath}/api/v1/account/discord/unlink`,
                { method: "POST", credentials: "include" },
            );
            const result = await response.json().catch(() => null);
            if (!response.ok || result?.ok === false) {
                throw new Error(
                    result?.message || "Failed to disconnect Discord.",
                );
            }

            if (typeof actions?.refreshSessionUser === "function") {
                await actions.refreshSessionUser();
            }
            toast.success("Discord disconnected successfully");
        } catch (error) {
            toast.error(error?.message || "Failed to disconnect Discord.");
        } finally {
            setUnlinking(false);
        }
    }, [actions, unlinking]);

    const handleConfirmDisconnect = useCallback(() => {
        setDisconnectDialogOpen(false);
        handleDisconnect();
    }, [handleDisconnect]);

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1.25rem",
                    width: "100%",
                    [ACCOUNT_PAGE_MOBILE_MQ]: {
                        flexDirection: "column",
                        alignItems: "stretch",
                    },
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h3" sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>
                        Discord
                    </Typography>
                    <Typography
                        sx={{
                            color: connected
                                ? colors.state.success.main
                                : colors.text.muted,
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            lineHeight: 1.4,
                            mt: "0.35rem",
                        }}
                    >
                        {connected
                            ? `Connected as ${displayName}`
                            : "Not connected"}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    type="button"
                    aria-label={
                        connected ? "Disconnect Discord" : "Connect Discord"
                    }
                    disabled={unlinking}
                    aria-disabled={unlinking}
                    onClick={
                        connected
                            ? () => setDisconnectDialogOpen(true)
                            : handleConnect
                    }
                    sx={[
                        connected
                            ? {
                                  ...LOBBY_EXIT_BUTTON_SX,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "0.5rem",
                                  flexShrink: 0,
                                  ml: "auto",
                                  minHeight: "2.35rem",
                              }
                            : ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
                        {
                            [ACCOUNT_PAGE_MOBILE_MQ]: {
                                width: "100%",
                                ml: 0,
                            },
                        },
                        unlinking
                            ? ACCOUNT_PAGE_SEND_CODE_BUTTON_DISABLED_SX
                            : null,
                    ]}
                >
                    <Box component="span">
                        {unlinking
                            ? "Disconnecting"
                            : connected
                              ? "Disconnect"
                              : "Connect"}
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        <DiscordIcon />
                    </Box>
                </Box>
            </Box>

            <ConfirmDialog
                open={disconnectDialogOpen}
                title="Disconnect Discord"
                message="Are you sure you want to disconnect your Discord account from Ouro?"
                confirmText="Disconnect"
                cancelText="Cancel"
                onConfirm={handleConfirmDisconnect}
                onCancel={() => setDisconnectDialogOpen(false)}
            />
        </>
    );
}

DiscordField.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
};

export default function PublicProfilePage({ user, actions, avatarCacheBust }) {
    const [appearanceOpen, setAppearanceOpen] = useState(true);
    const [socialOpen, setSocialOpen] = useState(true);
    const [editingField, setEditingField] = useState("");
    const [draftValue, setDraftValue] = useState("");
    const [savingField, setSavingField] = useState("");
    const appearanceContentId = "public-profile-appearance-content";
    const socialContentId = "public-profile-social-details-content";
    const AppearanceArrowIcon = appearanceOpen
        ? KeyboardArrowUpRounded
        : KeyboardArrowDownRounded;
    const SocialArrowIcon = socialOpen
        ? KeyboardArrowUpRounded
        : KeyboardArrowDownRounded;
    const values = useMemo(
        () =>
            SOCIAL_FIELDS.reduce(
                (result, field) => ({
                    ...result,
                    [field.key]: getStringValue(
                        user?.socialDetails?.[field.key] ?? user?.[field.key],
                    ),
                }),
                {},
            ),
        [user],
    );

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const linked = params.get("discord_linked") === "1";
            const errorReason = params.get("discord_error");
            if (!linked && !errorReason) return;

            if (linked) {
                toast.success("Discord connected successfully");
                if (typeof actions?.refreshSessionUser === "function") {
                    Promise.resolve(actions.refreshSessionUser()).catch(() => {});
                }
            } else {
                toast.error(
                    DISCORD_ERROR_MESSAGES[errorReason] ||
                        "Discord could not be connected. Please try again.",
                );
            }

            params.delete("discord_linked");
            params.delete("discord_error");
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}${
                    params.toString() ? `?${params}` : ""
                }${window.location.hash}`,
            );
        } catch (_) {
            // Query cleanup and feedback are best-effort only.
        }
    }, [actions]);

    const startEditing = useCallback(
        (fieldKey) => {
            setEditingField(fieldKey);
            setDraftValue(values[fieldKey] || "");
        },
        [values],
    );

    const cancelEditing = useCallback(() => {
        setEditingField("");
        setDraftValue("");
    }, []);

    const saveField = useCallback(
        async (field) => {
            if (
                savingField ||
                typeof actions?.submitAccountChanges !== "function"
            ) {
                if (!savingField) {
                    toast.error("Unable to save public profile changes.");
                }
                return;
            }

            const nextValue = draftValue.trim();
            setSavingField(field.key);
            try {
                const result = await actions.submitAccountChanges({
                    userId: user?.userId,
                    socialDetails: {
                        [field.key]: nextValue,
                    },
                });
                if (result?.type !== "ACCOUNT_UPDATE_COMPLETE") {
                    throw new Error(
                        result?.text ||
                            "Failed to save public profile changes.",
                    );
                }

                setEditingField("");
                setDraftValue("");
                toast.success(`${field.label} saved successfully`);
            } catch (error) {
                toast.error(
                    error?.message || "Failed to save public profile changes.",
                );
            } finally {
                setSavingField("");
            }
        },
        [actions, draftValue, savingField, user?.userId],
    );

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Public Profile
                    </Typography>
                </Box>

                <Box sx={ACCOUNT_PAGE_SUBSECTION_SX}>
                    <Typography component="h2" sx={{ m: 0, p: 0 }}>
                        <Box
                            component="button"
                            type="button"
                            aria-expanded={appearanceOpen}
                            aria-controls={appearanceContentId}
                            onClick={() =>
                                setAppearanceOpen((current) => !current)
                            }
                            sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                        >
                            <Box
                                component="span"
                                sx={ACCOUNT_PAGE_SUBSECTION_TITLE_SX}
                            >
                                Appearance
                            </Box>
                            <AppearanceArrowIcon
                                aria-hidden
                                sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX}
                            />
                        </Box>
                    </Typography>

                    <Collapse
                        in={appearanceOpen}
                        timeout={SUBSECTION_COLLAPSE_TIMEOUT}
                        easing={SUBSECTION_COLLAPSE_EASING}
                    >
                        <Box
                            id={appearanceContentId}
                            sx={{ mt: "1.25rem" }}
                        >
                            <ProfilePictureEditor
                                user={user}
                                actions={actions}
                                avatarCacheBust={avatarCacheBust}
                            />
                        </Box>
                    </Collapse>

                    <Box sx={ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX} />
                </Box>

                <Box>
                    <Typography component="h2" sx={{ m: 0, p: 0 }}>
                        <Box
                            component="button"
                            type="button"
                            aria-expanded={socialOpen}
                            aria-controls={socialContentId}
                            onClick={() => setSocialOpen((current) => !current)}
                            sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                        >
                            <Box
                                component="span"
                                sx={ACCOUNT_PAGE_SUBSECTION_TITLE_SX}
                            >
                                Social details
                            </Box>
                            <SocialArrowIcon
                                aria-hidden
                                sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX}
                            />
                        </Box>
                    </Typography>

                    <Collapse
                        in={socialOpen}
                        timeout={SUBSECTION_COLLAPSE_TIMEOUT}
                        easing={SUBSECTION_COLLAPSE_EASING}
                    >
                        <Box id={socialContentId} sx={{ mt: "1.25rem" }}>
                            {SOCIAL_FIELDS.map((field, index) => (
                                <React.Fragment key={field.key}>
                                    {index > 0 ? (
                                        <Box
                                            sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX}
                                        />
                                    ) : null}
                                    <SocialDetailField
                                        field={field}
                                        value={values[field.key]}
                                        editing={editingField === field.key}
                                        draftValue={
                                            editingField === field.key
                                                ? draftValue
                                                : ""
                                        }
                                        saving={savingField === field.key}
                                        onEdit={() => startEditing(field.key)}
                                        onDraftChange={setDraftValue}
                                        onSave={() => saveField(field)}
                                        onCancel={cancelEditing}
                                    />
                                </React.Fragment>
                            ))}

                            <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />
                            <DiscordField user={user} actions={actions} />
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        </Box>
    );
}

PublicProfilePage.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
    avatarCacheBust: PropTypes.number,
};
